/* RESPONSIBLE TEAM: team-inbox-experience */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable @intercom/intercom/no-bare-strings */
// @ts-ignore
import DefaultAvatars from '@intercom/pulse/lib/default-avatars';

export const UNASSIGNED_ID = 0;

// BPO separation constants - matches the Ruby constants in Inbox::Constants::BpoSeparation
import { REDACTED_ADMIN_NAME, REDACTED_ADMIN_URL } from 'embercom/lib/inbox/constants';

const PALETTE = [
  'fd3a57',
  'ffb3c6',
  'fa7080',
  'f0b3fc',
  'c701f1',
  '9401b2',
  '261860',
  '286efa',
  '6b9cff',
  'd3e2fd',
  '8fe3eb',
  '0accac',
  '17c65d',
  '8ae2ad',
  'b8e65c',
  'f7d50b',
  'ffb848',
  'ff8b47',
  '888888',
  'c8c6c4',
  'e5e5e5',
];

export interface WorkloadManagementSettings {
  assignmentLimit?: number;
  ticketAssignmentLimit?: number;
  channelAssignmentLimits?: Record<string, number | null> | null;
}

export interface CountMetadata {
  tickets?: number;
  conversations?: number;
  channels?: Record<string, number | null> | null;
}

export interface AdminSummaryWireFormat {
  id: number;
  name?: string;
  image_url?: string | null;
  image_dark_url?: string | null;
  alias_enabled?: boolean;
  alias_name?: string;
  alias_email?: string;
  is_impersonating?: boolean;
  is_away?: boolean;
  admin_status?: {
    away_mode_enabled?: boolean;
    reassign_conversations?: boolean;
    is_auto_changed?: boolean;
    channel_availability?: string;
  };
  count?: number;
  count_metadata?: {
    tickets?: number;
    conversations?: number;
    channels?: Record<string, number | null> | null;
  };
  team_assignment_count?: {} | Record<number, number>;
  team_assignment_count_metadata?: {} | Record<number, CountMetadata>;
  email?: string;
  workload_management_settings?: {
    assignment_limit?: number;
    ticket_assignment_limit?: number;
    // Backend stores per-teammate channel caps under `channel_limits` (same
    // field the teammate-workload UI reads/writes). Per-channel values can be
    // null, meaning "no limit set" for that channel.
    channel_limits?: Record<string, number | null> | null;
  };
  wlm_inbox_priority_level?: number;
  restricted_contexts?: number[];
  removed?: boolean;
  is_operator?: boolean;
  is_redacted?: boolean;
}

export class AdminStatus {
  awayModeEnabled: boolean | undefined;
  reassignConversations: boolean | undefined;
  isAutoChanged: boolean | undefined;
  channelAvailability: string | undefined;

  constructor(
    awayModeEnabled: boolean | undefined,
    reassignConversations: boolean | undefined,
    isAutoChanged: boolean | undefined,
    channelAvailability: string | undefined,
  ) {
    this.awayModeEnabled = awayModeEnabled;
    this.reassignConversations = reassignConversations;
    this.isAutoChanged = isAutoChanged;
    this.channelAvailability = channelAvailability;
  }
}

export default class AdminSummary {
  readonly id: number;
  readonly name: string;
  readonly imageURL: string;
  readonly imageDarkURL?: string;
  readonly aliasEnabled?: boolean;
  readonly aliasName?: string;
  readonly aliasEmail?: string;
  readonly isImpersonating?: boolean;
  readonly isAway?: boolean;
  readonly openCount?: number;
  readonly countMetadata?: CountMetadata;
  readonly teamAssignmentCount?: {} | Record<number, number>;
  readonly teamAssignmentCountMetadata?: {} | Record<number, CountMetadata>;
  readonly email?: string;
  readonly workloadManagementSettings?: WorkloadManagementSettings;
  readonly wlmInboxPriorityLevel?: number;
  readonly restrictedContexts?: number[];
  adminStatus?: AdminStatus;
  readonly removed?: boolean;
  readonly isOperator?: boolean;
  readonly isRedacted?: boolean;

  constructor(
    id: number,
    name: string,
    imageURL: string | null,
    aliasEnabled?: boolean,
    aliasName?: string,
    aliasEmail?: string,
    isImpersonating?: boolean,
    isAway?: boolean,
    openCount?: number,
    countMetadata?: CountMetadata,
    email?: string,
    workloadManagementSettings?: WorkloadManagementSettings,
    wlmInboxPriorityLevel?: number,
    restrictedContexts?: Array<number>,
    adminStatus?: AdminStatus,
    removed?: boolean,
    isOperator?: boolean,
    isRedacted?: boolean,
    teamAssignmentCount?: {} | Record<number, number>,
    teamAssignmentCountMetadata?: {} | Record<number, CountMetadata>,
    imageDarkURL?: string | null,
  ) {
    this.id = id;
    this.name = name;
    this.imageURL = imageURL || DefaultAvatars.admins[128];
    this.imageDarkURL = imageDarkURL ?? undefined;
    this.aliasEnabled = aliasEnabled;
    this.aliasName = aliasName;
    this.aliasEmail = aliasEmail;
    this.isImpersonating = isImpersonating;
    this.isAway = isAway;
    this.openCount = openCount;
    this.countMetadata = countMetadata;
    this.teamAssignmentCount = teamAssignmentCount;
    this.teamAssignmentCountMetadata = teamAssignmentCountMetadata;
    this.email = email;
    this.workloadManagementSettings = workloadManagementSettings;
    this.wlmInboxPriorityLevel = wlmInboxPriorityLevel;
    this.restrictedContexts = restrictedContexts;
    this.adminStatus = adminStatus;
    this.removed = removed;
    this.isOperator = isOperator;
    this.isRedacted = isRedacted;
  }

  static deserialize(json: AdminSummaryWireFormat): AdminSummary {
    let adminStatus = new AdminStatus(
      json.admin_status?.away_mode_enabled,
      json.admin_status?.reassign_conversations,
      json.admin_status?.is_auto_changed,
      json.admin_status?.channel_availability,
    );
    return new AdminSummary(
      json.id,
      json.name ?? REDACTED_ADMIN_NAME,
      json.image_url ?? REDACTED_ADMIN_URL,
      json.alias_enabled,
      json.alias_name,
      json.alias_email,
      json.is_impersonating,
      json.is_away,
      json.count,
      {
        tickets: json.count_metadata?.tickets,
        conversations: json.count_metadata?.conversations,
        channels: json.count_metadata?.channels,
      },
      json.email,
      {
        assignmentLimit: json.workload_management_settings?.assignment_limit,
        ticketAssignmentLimit: json.workload_management_settings?.ticket_assignment_limit,
        channelAssignmentLimits: json.workload_management_settings?.channel_limits,
      },
      json.wlm_inbox_priority_level,
      json.restricted_contexts,
      adminStatus,
      json.removed,
      json.is_operator,
      json.is_redacted,
      json.team_assignment_count,
      json.team_assignment_count_metadata as any,
      json.image_dark_url,
    );
  }

  static get unassigned() {
    return new AdminSummary(UNASSIGNED_ID, 'Unassigned', null);
  }

  clone() {
    return new AdminSummary(
      this.id,
      this.name,
      this.imageURL,
      this.aliasEnabled,
      this.aliasName,
      this.aliasEmail,
      this.isImpersonating,
      this.isAway,
      this.openCount,
      this.countMetadata,
      this.email,
      this.workloadManagementSettings,
      this.wlmInboxPriorityLevel,
      this.restrictedContexts,
      this.adminStatus,
      this.removed,
      this.isOperator,
      this.isRedacted,
      this.teamAssignmentCount,
      this.teamAssignmentCountMetadata,
      this.imageDarkURL,
    );
  }

  get translationKey(): string | void {
    if (this.isUnassignedAssignee) {
      return 'inbox.unassigned';
    }
  }

  get isUnassignedAssignee(): boolean {
    return this.id === UNASSIGNED_ID;
  }

  get color(): string {
    return PALETTE[this.id % PALETTE.length];
  }

  get initials(): string {
    return this.name
      .split(' ')
      .map((part) => part[0])
      .join('');
  }

  /**
   * Get team-specific assignment count for a given team ID
   */
  getTeamAssignmentCount(teamId: number): number {
    if (!this.teamAssignmentCount || Object.keys(this.teamAssignmentCount).length === 0) {
      return 0;
    }
    let teamCounts = this.teamAssignmentCount as Record<number, number>;
    return teamCounts[teamId] || 0;
  }

  /**
   * Get team-specific assignment count metadata for a given team ID
   */
  getTeamAssignmentCountMetadata(teamId: number): CountMetadata | undefined {
    if (
      !this.teamAssignmentCountMetadata ||
      Object.keys(this.teamAssignmentCountMetadata).length === 0
    ) {
      return undefined;
    }
    let teamMetadata = this.teamAssignmentCountMetadata as Record<number, CountMetadata>;
    return teamMetadata[teamId] || undefined;
  }

  get firstName(): string {
    return this.name.split(' ')[0];
  }
}
